// Top-level router for TRIMIE web.
// Phase 1 routing changes (docs/design-spec.md §4):
// - 5 tabs: home / closet / tryon (center FAB) / looks / me
// - Profile is the "me" tab (no longer a separate fullscreen modal)
// - Add Clothing is reached from inside Closet, not the main nav
// - Welcome → Sign Up → Add Photo → Sizing → Home onboarding flow

import { useState, useEffect } from 'react';
import AuthPage from './AuthPage';
import InvitePage from './InvitePage';
import OnboardingPhotoPage from './OnboardingPhotoPage';
import OnboardingSizingPage from './OnboardingSizingPage';
import HomePage from './HomePage';
import ClosetPage from './ClosetPage';
import AddClothingPage from './AddClothingPage';
import TryOnPage from './TryOnPage';
import MyLooksPage from './MyLooksPage';
import ProfilePage from './ProfilePage';
import AvatarBuilderPage from './AvatarBuilderPage';
import BottomNav from './BottomNav';
import api from './api';
import './App.css';

const ONBOARDING_KEY = 'trimie_onboarded_v2';  // bumped from lichic_onboarded — adds sizing step

export default function App() {
  const [authed, setAuthed] = useState(!!localStorage.getItem('token'));
  const [onbStep, setOnbStep] = useState(null);  // null | 'photo' | 'sizing'
  const [screen, setScreen] = useState('home');
  const [navContext, setNavContext] = useState({});
  const [user, setUser] = useState(null);
  const [closetCount, setClosetCount] = useState(null);
  const [tryonCount, setTryonCount] = useState(null);

  useEffect(() => {
    if (!authed) return;
    api.get('/auth/profile/')
      .then(res => setUser(res.data?.data ?? null))
      .catch(() => {});
    api.get('/tryon/upload-row-clothes/')
      .then(res => {
        const data = res.data?.data?.results ?? res.data?.data ?? [];
        setClosetCount(Array.isArray(data) ? data.length : 0);
      })
      .catch(() => {});
    api.get('/tryon/image/')
      .then(res => {
        const data = res.data?.data?.results ?? res.data?.data ?? [];
        setTryonCount(Array.isArray(data) ? data.length : 0);
      })
      .catch(() => {});
  }, [authed]);

  const handleLogin = () => {
    setAuthed(true);
    setOnbStep(null);
    setScreen('home');
  };

  // Called after a successful signup. New users walk through:
  // photo (step 1) → sizing (step 2) → Home. Tracked via localStorage so
  // returning signed-up users skip onboarding.
  const handleSignedUp = () => {
    setAuthed(true);
    if (!localStorage.getItem(ONBOARDING_KEY)) {
      setOnbStep('photo');
    } else {
      setScreen('home');
    }
  };

  const finishOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    setOnbStep(null);
    setScreen('home');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setAuthed(false);
    setUser(null);
    setOnbStep(null);
    setScreen('home');
    setNavContext({});
  };

  const handleNav = (s, ctx = {}) => {
    setNavContext(ctx);
    setScreen(s);
  };

  const handleGarmentAdded = () => setClosetCount(c => (c ?? 0) + 1);

  // Lightweight URL-based routing for the invite redemption flow. No router lib
  // — we just peek at window.location at unauth time. /invite/<token> shows the
  // signup form pre-filled with the invited email; on success it strips the URL
  // and lands on Home like any other login.
  if (!authed) {
    const m = window.location.pathname.match(/^\/invite\/([^/]+)\/?$/);
    if (m) {
      return (
        <InvitePage
          token={m[1]}
          onLogin={handleLogin}
          onBackToWelcome={() => { window.history.replaceState({}, '', '/'); setAuthed(false); }}
        />
      );
    }
    return <AuthPage onLogin={handleLogin} onSignedUp={handleSignedUp} />;
  }

  // Onboarding flow: photo → sizing → done. After photo upload, re-fetch the
  // user so bodyPhotoUrl is in state when Home renders — otherwise the freshly
  // uploaded onboarding photo appears missing until next reload.
  const refreshUserThen = (next) => {
    api.get('/auth/profile/')
      .then(res => setUser(res.data?.data ?? null))
      .catch(() => {})
      .finally(next);
  };

  if (onbStep === 'photo') {
    return (
      <OnboardingPhotoPage
        onDone={() => refreshUserThen(() => setOnbStep('sizing'))}
        onSkip={() => setOnbStep('sizing')}
        onBuildAvatar={() => setOnbStep('avatar')}
      />
    );
  }
  if (onbStep === 'avatar') {
    // Onboarding variant of the avatar builder: completing it refreshes the
    // user (so bodyPhotoUrl lands in state) and then advances to sizing.
    return (
      <AvatarBuilderPage
        onNav={() => refreshUserThen(() => setOnbStep('sizing'))}
        user={user}
        onUserUpdate={setUser}
      />
    );
  }
  if (onbStep === 'sizing') {
    return (
      <OnboardingSizingPage
        onDone={finishOnboarding}
        onSkip={finishOnboarding}
      />
    );
  }

  // Body photo is the model input for try-on. profilePhotoUrl (the avatar)
  // must NEVER stand in for it — they're conceptually different photos.
  const savedModelUrl = user?.bodyPhotoUrl || null;

  const renderScreen = () => {
    switch (screen) {
      case 'home':
        return <HomePage onNav={handleNav} user={user} closetCount={closetCount} tryonCount={tryonCount} />;
      case 'tryon':
        return <TryOnPage onNav={handleNav} savedModelUrl={savedModelUrl} navContext={navContext} />;
      case 'closet':
        return <ClosetPage onNav={handleNav} />;
      case 'add':
        return <AddClothingPage onNav={handleNav} onAdded={handleGarmentAdded} />;
      case 'looks':
        return <MyLooksPage onNav={handleNav} />;
      case 'avatar-builder':
        return <AvatarBuilderPage onNav={handleNav} user={user} onUserUpdate={setUser} />;
      case 'me':
      case 'profile':  // backwards-compat for any lingering onNav('profile') calls
        return (
          <ProfilePage
            onNav={handleNav}
            user={user}
            onUserUpdate={setUser}
            onLogout={handleLogout}
            closetCount={closetCount}
            tryonCount={tryonCount}
          />
        );
      default:
        return <HomePage onNav={handleNav} user={user} closetCount={closetCount} tryonCount={tryonCount} />;
    }
  };

  // BottomNav is now permanent — all 5 tabs always visible.
  // The center FAB highlights when screen === 'tryon'.
  // Map legacy 'profile' onto the 'me' tab for highlight purposes.
  const navScreen = screen === 'profile' ? 'me' : screen;
  // 'add' is reached from inside Closet, not the tab bar — show Closet as active.
  const highlightedScreen = navScreen === 'add' ? 'closet' : navScreen;

  return (
    <div className="phone-shell">
      {renderScreen()}
      <BottomNav screen={highlightedScreen} onNav={handleNav} />
    </div>
  );
}
